import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { DeviceLink } from "../../components/devices/DeviceLink";
import { DevicePicker } from "../../components/devices/DevicePicker";
import { Alert } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { FormField } from "../../components/ui/FormField";
import { Glossary } from "../../components/ui/Glossary";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { Switch } from "../../components/ui/Switch";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import {
  type DeviceGroupMemberRow,
  type DeviceGroupRow,
  deviceGroupsApi,
} from "../../lib/api/network/deviceGroups";
import { downloadCsv, rowsToCsv } from "../../lib/csv";
import { memberDisplayName } from "../../lib/devices/display";
import { formatMessage, t } from "../../lib/i18n";
import { useRecompileNotice } from "../../lib/recompileNotice";
import { useLocale } from "../../lib/useLocale";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

interface DeviceGroupsPageProps {
  embedded?: boolean;
}

export function DeviceGroupsPage({ embedded = false }: DeviceGroupsPageProps = {}) {
  useLocale();
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const { push: pushToast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [editRow, setEditRow] = useState<DeviceGroupRow | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editActive, setEditActive] = useState(true);

  const [pendingDelete, setPendingDelete] = useState<DeviceGroupRow | null>(null);

  const [selected, setSelected] = useState<DeviceGroupRow | null>(null);

  const list = useQuery({
    queryKey: ["network", "device-groups"],
    queryFn: deviceGroupsApi.list,
  });

  const create = useMutation({
    mutationFn: () => deviceGroupsApi.create({ name, description }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "device-groups"] });
      setCreateOpen(false);
      setName("");
      setDescription("");
    },
  });

  const editMut = useMutation({
    mutationFn: (vars: { id: string; body: { name?: string; description?: string; is_active?: boolean } }) =>
      deviceGroupsApi.patch(vars.id, vars.body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "device-groups"] });
      setEditRow(null);
      notice();
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deviceGroupsApi.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["network", "device-groups"] });
      setPendingDelete(null);
      notice();
    },
    onError: (err) =>
      pushToast({ tone: "danger", title: t("errors.generic"), body: translateError(err) }),
  });

  function openEdit(row: DeviceGroupRow) {
    setEditRow(row);
    setEditName(row.name);
    setEditDescription(row.description);
    setEditActive(row.is_active);
  }

  function exportCsv() {
    if (!list.data) return;
    const csv = rowsToCsv(list.data, [
      { header: "name", value: (r) => r.name },
      { header: "description", value: (r) => r.description },
      { header: "is_default", value: (r) => r.is_default },
      { header: "is_active", value: (r) => r.is_active },
      { header: "members_count", value: (r) => r.members_count },
      { header: "rule_usage_count", value: (r) => r.rule_usage_count },
      { header: "created_at", value: (r) => r.created_at },
    ]);
    downloadCsv(`device-groups-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  }

  const columns: Column<DeviceGroupRow>[] = [
    {
      key: "name",
      header: t("network.deviceGroups.column.name"),
      render: (row) => (
        <button
          type="button"
          onClick={() => setSelected(row)}
          style={{
            background: "none",
            border: "none",
            color: "var(--accent, #2563eb)",
            cursor: "pointer",
            padding: 0,
            textAlign: "left",
          }}
        >
          <div>{row.name}</div>
          {row.description && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
              {row.description}
            </div>
          )}
        </button>
      ),
    },
    {
      key: "default",
      header: t("network.deviceGroups.column.default"),
      render: (row) =>
        row.is_default ? (
          <Badge tone="info">{t("network.deviceGroups.defaultBadge")}</Badge>
        ) : (
          "—"
        ),
    },
    {
      key: "members",
      header: t("network.deviceGroups.column.members"),
      render: (row) => <span className="num">{row.members_count}</span>,
      align: "right",
    },
    {
      key: "rule_usage",
      header: t("network.deviceGroups.column.usedInRules"),
      render: (row) =>
        row.rule_usage_count > 0 ? (
          <Link
            to={`/policies/rules?deviceGroup=${encodeURIComponent(row.id)}`}
            className="badge badge--info"
            style={{ textDecoration: "none" }}
          >
            {row.rule_usage_count} {t("network.deviceGroups.rulesShort")}
          </Link>
        ) : (
          <span className="app-pkg">{t("network.deviceGroups.notUsed")}</span>
        ),
      align: "right",
    },
    {
      key: "active",
      header: t("network.deviceGroups.column.active"),
      render: (row) =>
        row.is_active ? (
          <Badge tone="success">{t("common.yes")}</Badge>
        ) : (
          <Badge tone="neutral">{t("common.no")}</Badge>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <Button variant="secondary" size="sm" onClick={() => setSelected(row)}>
            {t("network.deviceGroups.action.manage")}
          </Button>
          {!row.is_default && (
            <>
              <Button variant="ghost" size="sm" onClick={() => openEdit(row)}>
                {t("network.deviceGroups.action.edit")}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => setPendingDelete(row)}
              >
                {t("network.deviceGroups.action.delete")}
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow={t("network.deviceGroups.eyebrow")}
          title={
            <span style={{ display: "inline-flex", alignItems: "center" }}>
              {t("network.deviceGroups.title")}
              <Glossary termKey="deviceGroup" />
            </span>
          }
          subtitle={t("network.deviceGroups.subtitle")}
          actions={
            <>
              <Button variant="secondary" onClick={exportCsv} disabled={!list.data}>
                {t("network.deviceGroups.action.exportCsv")}
              </Button>
              <Button onClick={() => setCreateOpen(true)}>
                {t("network.deviceGroups.action.create")}
              </Button>
            </>
          }
        />
      )}

      {embedded && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            marginBottom: 16,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
              {t("network.deviceGroups.title")}
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
              {t("network.deviceGroups.subtitle")}
            </p>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="secondary" onClick={exportCsv} disabled={!list.data}>
              {t("network.deviceGroups.action.exportCsv")}
            </Button>
            <Button onClick={() => setCreateOpen(true)}>
              {t("network.deviceGroups.action.create")}
            </Button>
          </div>
        </div>
      )}

      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(list.error)}
          />
        )}
        {list.data && (
          <Table
            rows={list.data}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                titleKey="network.deviceGroups.emptyTitle"
                bodyKey="network.deviceGroups.emptyBody"
              />
            }
          />
        )}
      </Card>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("network.deviceGroups.create.title")}
      >
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {create.error && <Alert tone="danger">{translateError(create.error)}</Alert>}
          <FormField label={t("network.deviceGroups.create.field.name")} required>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            )}
          </FormField>
          <FormField label={t("network.deviceGroups.create.field.description")}>
            {(id) => (
              <Input
                id={id}
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </FormField>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="ghost" type="button" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t("network.deviceGroups.create.submit")}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={editRow !== null}
        onClose={() => setEditRow(null)}
        title={
          editRow
            ? formatMessage("network.deviceGroups.edit.title", { name: editRow.name })
            : ""
        }
      >
        {editRow && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              editMut.mutate({
                id: editRow.id,
                body: {
                  name: editName,
                  description: editDescription,
                  is_active: editActive,
                },
              });
            }}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {editMut.error && <Alert tone="danger">{translateError(editMut.error)}</Alert>}
            <FormField label={t("network.deviceGroups.create.field.name")} required>
              {(id) => (
                <Input id={id} type="text" value={editName} onChange={(e) => setEditName(e.target.value)} required />
              )}
            </FormField>
            <FormField label={t("network.deviceGroups.create.field.description")}>
              {(id) => (
                <Input
                  id={id}
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              )}
            </FormField>
            <Switch
              checked={editActive}
              onChange={setEditActive}
              label={t("common.active")}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button variant="ghost" type="button" onClick={() => setEditRow(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={editMut.isPending}>
                {t("network.deviceGroups.edit.submit")}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={
          pendingDelete
            ? formatMessage("network.deviceGroups.delete.title", { name: pendingDelete.name })
            : ""
        }
        body={
          pendingDelete
            ? pendingDelete.rule_usage_count > 0
              ? formatMessage("network.deviceGroups.delete.body", {
                  usage: String(pendingDelete.rule_usage_count),
                })
              : t("network.deviceGroups.delete.bodyNoUsage")
            : ""
        }
        confirmLabel={t("network.deviceGroups.delete.confirm")}
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) remove.mutate(pendingDelete.id);
        }}
      />

      <Modal
        open={selected !== null}
        onClose={() => setSelected(null)}
        size="lg"
        title={
          selected
            ? formatMessage("network.deviceGroups.manage.title", { name: selected.name })
            : ""
        }
      >
        {selected && (
          <DeviceGroupMembersPanel
            group={selected}
            onError={(msg) => pushToast({ tone: "danger", title: t("errors.generic"), body: msg })}
          />
        )}
      </Modal>
    </div>
  );
}

function DeviceGroupMembersPanel({
  group,
  onError,
}: {
  group: DeviceGroupRow;
  onError: (msg: string) => void;
}) {
  const qc = useQueryClient();
  const notice = useRecompileNotice();
  const [removeMember, setRemoveMember] = useState<DeviceGroupMemberRow | null>(null);
  const [search, setSearch] = useState("");

  const members = useQuery({
    queryKey: ["network", "device-groups", group.id, "members"],
    queryFn: () => deviceGroupsApi.listMembers(group.id),
  });

  const invalidate = () => {
    void qc.invalidateQueries({
      queryKey: ["network", "device-groups", group.id, "members"],
    });
    void qc.invalidateQueries({ queryKey: ["network", "device-groups"] });
  };

  const add = useMutation({
    mutationFn: (deviceId: string) => deviceGroupsApi.addMember(group.id, { device: deviceId }),
    onSuccess: () => {
      invalidate();
      notice();
    },
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => deviceGroupsApi.removeMember(group.id, memberId),
    onSuccess: () => {
      invalidate();
      setRemoveMember(null);
      notice();
    },
    onError: (err) => onError(translateError(err)),
  });

  const memberDeviceIds = members.data?.map((m) => m.device) ?? [];

  const filteredMembers = (members.data ?? []).filter((m) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.device_uid?.toLowerCase().includes(q) ||
      m.user_email?.toLowerCase().includes(q) ||
      m.user_full_name?.toLowerCase().includes(q) ||
      m.enrollee_full_name?.toLowerCase().includes(q)
    );
  });

  const statusToTone = (status: string): "neutral" | "success" | "warning" | "danger" => {
    if (status === "active") return "success";
    if (status === "disabled" || status === "deprovisioned") return "danger";
    if (status === "awaiting_policy" || status === "pending_inventory") return "warning";
    return "neutral";
  };

  const columns: Column<DeviceGroupMemberRow>[] = [
    {
      key: "user",
      header: t("network.deviceGroups.member.column.user"),
      render: (row) => (
        <div>
          <div>{memberDisplayName(row)}</div>
          <DeviceLink
            deviceId={row.device}
            deviceUid={row.device_uid}
            className="app-pkg"
          />
        </div>
      ),
    },
    {
      key: "platform",
      header: t("network.deviceGroups.member.column.platform"),
      render: (row) => (
        <span className="badge badge--info">{t(`common.platform.${row.platform}`)}</span>
      ),
    },
    {
      key: "status",
      header: t("network.deviceGroups.member.column.status"),
      render: (row) => (
        <Badge tone={statusToTone(row.status)}>
          {t(`devices.lifecycleStatus.${row.status}`, row.status)}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) =>
        group.is_default ? null : (
          <Button
            variant="ghost"
            size="sm"
            loading={remove.isPending}
            onClick={() => setRemoveMember(row)}
          >
            {t("network.deviceGroups.member.action.remove")}
          </Button>
        ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="kv-grid" style={{ borderRadius: 8 }}>
        <div className="kv">
          <div className="kv__label">{t("network.deviceGroups.column.members")}</div>
          <div className="kv__value">{members.data?.length ?? 0}</div>
        </div>
        <div className="kv">
          <div className="kv__label">{t("network.deviceGroups.column.usedInRules")}</div>
          <div className="kv__value">{group.rule_usage_count}</div>
        </div>
        <div className="kv">
          <div className="kv__label">{t("common.status")}</div>
          <div className="kv__value">
            {group.is_active ? t("common.active") : t("common.inactive")}
          </div>
        </div>
      </div>

      {!group.is_default && (
        <div>
          <p
            style={{
              margin: "0 0 8px",
              fontSize: 12,
              fontWeight: 500,
              color: "var(--color-text-muted)",
            }}
          >
            {t("network.deviceGroups.member.add.device")}
          </p>
          {add.error && <Alert tone="danger">{translateError(add.error)}</Alert>}
          <DevicePicker
            excludeDeviceIds={memberDeviceIds}
            disabled={add.isPending}
            onSelect={(device) => add.mutate(device.id)}
          />
        </div>
      )}
      {group.is_default && (
        <Alert tone="info">{t("network.deviceGroups.defaultHint")}</Alert>
      )}

      {(members.data?.length ?? 0) > 5 && (
        <Input
          type="search"
          placeholder={t("network.deviceGroups.member.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}

      {members.isLoading && <LoadingState />}
      {members.error && (
        <ErrorState
          titleKey="states.errorTitle"
          message={translateError(members.error)}
        />
      )}
      {members.data && (
        <Table
          rows={filteredMembers}
          columns={columns}
          rowKey={(row) => row.id}
          empty={
            <EmptyState
              titleKey="network.deviceGroups.member.emptyTitle"
              bodyKey="network.deviceGroups.member.emptyBody"
            />
          }
        />
      )}

      <ConfirmDialog
        open={removeMember !== null}
        title={t("network.deviceGroups.member.action.remove")}
        body={removeMember ? memberDisplayName(removeMember) : ""}
        tone="danger"
        loading={remove.isPending}
        onCancel={() => setRemoveMember(null)}
        onConfirm={() => {
          if (removeMember) remove.mutate(removeMember.id);
        }}
      />
    </div>
  );
}
